import { AiStars, Menu } from "@ignite-analytics/icons";
import { Box, Button, Collapse, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { type Answer, type AnswerUpdate, type AnswerValue, type Answers, isEmpty } from "./answers";
import type { Question as ContentsQuestion, QuestionnaireRevisionContents } from "./contents";
import { type DocumentType, DocumentTypesContext } from "./documentTypes";
import { FieldRequestsSection } from "./FieldRequestsSection";
import { type AnswerSuggestion, type Prefill, type Review, SuggestionsContext } from "./prefill";
import { PrefillPanel } from "./PrefillPanel";
import { Question } from "./Question";
import { ReadOnlyContext } from "./readOnly";
import { RichText } from "./RichText";
import { problems } from "./validation";
import { visibleQuestions, visibleSections } from "./visibility";

type Props = {
    contents: QuestionnaireRevisionContents;
    documentTypes?: DocumentType[];
    numbers?: Record<string, string>;
    intro?: React.ReactNode;
    header?: React.ReactNode;
    initialAnswers?: Answers;
    onChange?: (key: string, answer: Answer) => void;
    onSubmit?: (answers: Answers) => void | Promise<void>;
    readOnly?: boolean;
    prefill?: Prefill;
};

const singleQuestion = (question: ContentsQuestion) => {
    const targets = (question.when?.conditions ?? []).map((c) => ("question" in c ? c.question : undefined));
    return targets.length > 0 && targets.every((t) => t && t === targets[0]) ? targets[0] : undefined;
};

const followUps = (questions: ContentsQuestion[]) => {
    const parents = new Map<string, string>();
    const chain: string[] = [];
    for (const question of questions) {
        const target = singleQuestion(question);
        const nested = !!target && chain.includes(target);
        chain.length = 0;
        if (nested) {
            parents.set(question.id, target);
            for (let id: string | undefined = target; id; id = parents.get(id)) chain.push(id);
        }
        chain.unshift(question.id);
    }
    return parents;
};

const FIELD_REQUESTS = "fieldRequests";

const cardSx = {
    backgroundColor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "0 1px 2px rgba(13, 16, 23, 0.04)",
    overflow: "hidden",
} as const;

const NAV_ROOM = 300;
const CARDS_ROOM = 284;

export const Questionnaire: React.FC<Props> = ({
    contents,
    documentTypes,
    numbers,
    intro,
    header,
    initialAnswers,
    onChange,
    onSubmit,
    readOnly,
    prefill,
}) => {
    const [answers, setAnswers] = useState<Answers>(initialAnswers ?? {});
    const current = useRef(answers);
    const [active, setActive] = useState<string | null>(null);
    const locked = useRef(false);
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reviews, setReviews] = useState<Review[]>([]);
    const { formatMessage } = useIntl();
    const wide = useMediaQuery(useTheme().breakpoints.up("lg"));
    const row = useRef<HTMLDivElement>(null);
    const [rowBox, setRowBox] = useState({ width: 0, roomLeft: 0, roomRight: 0 });
    const [menuOpen, setMenuOpen] = useState(false);
    const shift = useRef(0);
    const [prompts, setPrompts] = useState<string[]>([]);
    const prompting = useCallback(
        (key: string, open: boolean) =>
            setPrompts((list) => (open ? [...list.filter((k) => k !== key), key] : list.filter((k) => k !== key))),
        []
    );

    const sections = visibleSections(contents, answers);
    const questions = visibleQuestions(contents, answers);
    const found = checked ? problems(contents, answers) : [];
    const sectionKey = sections.map((s) => s.id).join(",");

    useEffect(() => {
        const measure = () => {
            const rect = row.current?.getBoundingClientRect();
            const shifted = shift.current;
            if (rect)
                setRowBox({
                    width: rect.width,
                    roomLeft: rect.left - shifted,
                    roomRight: window.innerWidth - rect.right + shifted,
                });
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    useEffect(() => {
        const ids = sectionKey.split(",");
        let frame = 0;
        const update = () => {
            frame = 0;
            if (locked.current) return;
            const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
            const line = 80 + (window.innerHeight - 80) / 3;
            let next = ids[0];
            for (const id of ids) {
                const top = document.getElementById(`section-${id}`)?.getBoundingClientRect().top;
                if (top !== undefined && top <= line) next = id;
            }
            setActive(atBottom ? ids[ids.length - 1] : next);
        };
        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [sectionKey]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const focus = params.get("question");
        const question = focus && document.getElementById(`question-${focus}`);
        if (question) {
            question.scrollIntoView();
            return;
        }
        const section = params.get("section");
        const id =
            decodeURIComponent(window.location.hash.slice(1)) || (section && !/^\d+$/.test(section) ? section : "");
        if (id) document.getElementById(`section-${id}`)?.scrollIntoView();
    }, []);

    const goTo = (id: string) => {
        setActive(id);
        locked.current = true;
        const unlock = () => {
            locked.current = false;
        };
        window.addEventListener("scrollend", unlock, { once: true });
        window.setTimeout(unlock, 1000);
        document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(null, "", `#${encodeURIComponent(id)}`);
    };
    const change = (id: string, update: AnswerUpdate) => {
        const answer = typeof update === "function" ? update(current.current[id]) : update;
        current.current = { ...current.current, [id]: answer };
        setAnswers(current.current);
        setReviews((list) =>
            list.map((r) =>
                r.suggestion.questionKey !== id || JSON.stringify(r.suggestion.value) === JSON.stringify(answer.value)
                    ? r
                    : { ...r, status: "edited" }
            )
        );
        onChange?.(id, answer);
    };
    const applicable = (list: AnswerSuggestion[]) =>
        list.filter(
            (s) =>
                isEmpty(current.current[s.questionKey]?.value) &&
                contents.questions.some(
                    (q) => q.id === s.questionKey && q.type !== "file" && q.section !== FIELD_REQUESTS
                )
        );
    const suggest = (list: AnswerSuggestion[]) => {
        const fresh = applicable(list);
        for (const s of fresh) {
            current.current = {
                ...current.current,
                [s.questionKey]: { ...current.current[s.questionKey], value: s.value as AnswerValue },
            };
        }
        setAnswers(current.current);
        setReviews((existing) => [
            ...existing.filter((e) => !fresh.some((s) => s.questionKey === e.suggestion.questionKey)),
            ...fresh.map((suggestion) => ({ suggestion, status: "pending" as const })),
        ]);
        return fresh.length;
    };
    const confirm = (id: string) => {
        setReviews((list) =>
            list.map((r) => (r.suggestion.questionKey === id ? { ...r, status: "confirmed" as const } : r))
        );
        onChange?.(id, current.current[id]);
    };
    const reject = (id: string) => {
        current.current = { ...current.current, [id]: { ...current.current[id], value: undefined } };
        setAnswers(current.current);
        setReviews((list) => list.filter((r) => r.suggestion.questionKey !== id));
    };
    const shownReviews = questions.flatMap((q) => reviews.filter((r) => r.suggestion.questionKey === q.id));
    const pending = shownReviews.filter((r) => r.status === "pending");
    const nextPending = () =>
        document
            .getElementById(`question-${pending[0]?.suggestion.questionKey}`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
    const cardsInside = (reviews.length > 0 || prompts.length > 0) && rowBox.roomRight < 300;
    const collapsed = wide && cardsInside && sections.length > 1;
    const formWidth = rowBox.width - CARDS_ROOM;
    const side = (rowBox.roomLeft + rowBox.roomRight + CARDS_ROOM) / 2;
    const navOutside = wide && sections.length > 1 && side >= NAV_ROOM;
    shift.current = navOutside ? side - rowBox.roomLeft : 0;
    const sectionLinks = sections.map((s, i) => {
        const flagged = found.some((p) => contents.questions.find((q) => q.id === p.question)?.section === s.id);
        const isActive = (active ?? sections[0].id) === s.id;
        return (
            <Box
                key={s.id}
                component="a"
                href={`#${encodeURIComponent(s.id)}`}
                aria-current={isActive ? "location" : undefined}
                onClick={(event: React.MouseEvent) => {
                    event.preventDefault();
                    goTo(s.id);
                }}
                sx={{
                    py: 0.75,
                    pl: 1.5,
                    borderLeft: "2px solid",
                    borderColor: isActive ? "text.primary" : "divider",
                    textDecoration: "none",
                    transition: "border-color 150ms",
                    "&:hover": {
                        borderColor: isActive ? "text.primary" : "text.secondary",
                    },
                    "&:focus-visible": { outline: "2px solid", outlineOffset: 2 },
                }}
            >
                <Typography
                    variant="textSm"
                    fontWeight={isActive ? 600 : undefined}
                    color={flagged ? "textTextError" : isActive ? "textTextPrimary" : "textTextSecondary"}
                >
                    <FormattedMessage
                        defaultMessage="{number}. {title}"
                        values={{ number: numbers?.[s.id] ?? i + 1, title: s.name }}
                    />
                </Typography>
            </Box>
        );
    });
    const submit = () => {
        const all = problems(contents, answers);
        setChecked(true);
        if (pending.length > 0) {
            nextPending();
            return;
        }
        if (all.length === 0) {
            setSubmitting(true);
            Promise.resolve(onSubmit?.(answers)).finally(() => setSubmitting(false));
            return;
        }
        document.getElementById(`question-${all[0].question}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    return (
        <DocumentTypesContext.Provider value={documentTypes ?? []}>
            <ReadOnlyContext.Provider value={!!readOnly}>
                <SuggestionsContext.Provider
                    value={readOnly || !prefill ? undefined : { applicable, apply: suggest, prompting }}
                >
                    <Stack
                        ref={row}
                        direction="row"
                        sx={{ alignItems: "flex-start", position: "relative", left: shift.current }}
                    >
                        {navOutside && (
                            <Box sx={{ position: "sticky", top: 80, width: 0, flexShrink: 0 }}>
                                <Stack component="nav" sx={{ position: "absolute", top: 0, right: 24, width: 260 }}>
                                    {sectionLinks}
                                </Stack>
                            </Box>
                        )}
                        {collapsed && !navOutside && (
                            <Box sx={{ position: "sticky", top: 80, width: 0, flexShrink: 0, zIndex: 2 }}>
                                <Tooltip title={formatMessage({ defaultMessage: "Sections" })} placement="right">
                                    <IconButton
                                        size="small"
                                        aria-label={formatMessage({ defaultMessage: "Sections" })}
                                        aria-expanded={menuOpen}
                                        onClick={() => setMenuOpen((open) => !open)}
                                        sx={{ position: "absolute", top: 0, right: 8, color: "text.secondary" }}
                                    >
                                        <Menu fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        {sections.length > 1 && !navOutside && (
                            <Collapse
                                orientation="horizontal"
                                in={!collapsed || menuOpen}
                                sx={{
                                    display: { xs: "none", md: "block" },
                                    flexShrink: 0,
                                    position: "sticky",
                                    top: 80,
                                }}
                            >
                                <Stack component="nav" sx={{ width: 260, mr: 3 }}>
                                    {sectionLinks}
                                </Stack>
                            </Collapse>
                        )}
                        <Stack
                            component="fieldset"
                            disabled={readOnly}
                            spacing={3}
                            sx={{
                                flex: collapsed || navOutside ? "none" : 1,
                                width: navOutside ? formWidth : collapsed ? rowBox.width : undefined,
                                minWidth: 0,
                                boxSizing: "border-box",
                                m: 0,
                                p: 0,
                                pr: { lg: cardsInside && !navOutside ? `${CARDS_ROOM}px` : 0 },
                                border: 0,
                            }}
                        >
                            <Box sx={cardSx}>
                                {header}
                                {!header && (
                                    <Typography
                                        variant="textLg"
                                        fontWeight={600}
                                        sx={{ display: "block", px: { xs: 2, sm: 3 }, py: 2 }}
                                    >
                                        {contents.title}
                                    </Typography>
                                )}
                                {intro && (
                                    <Box
                                        sx={{
                                            px: { xs: 2, sm: 3 },
                                            py: 3,
                                            borderTop: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        {intro}
                                    </Box>
                                )}
                            </Box>
                            {prefill && !readOnly && (
                                <Box sx={cardSx}>
                                    <PrefillPanel prefill={prefill} onSuggestions={suggest} />
                                </Box>
                            )}
                            {sections.map((section, i) => {
                                const sectionQuestions = contents.questions.filter((q) => q.section === section.id);
                                const nested = followUps(sectionQuestions);
                                const shown = questions.filter((q) => q.section === section.id);
                                const documentCount = shown.filter(
                                    (q) => q.type === "file" && q.documentTypeIds?.length
                                ).length;
                                return (
                                    <Box
                                        key={section.id}
                                        id={`section-${section.id}`}
                                        sx={{ ...cardSx, overflow: "visible", scrollMarginTop: 80 }}
                                    >
                                        <Stack
                                            spacing={0.5}
                                            sx={{
                                                px: { xs: 2, sm: 3 },
                                                py: 2,
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <Typography variant="textMd" fontWeight={600}>
                                                <FormattedMessage
                                                    defaultMessage="{number}. {title}"
                                                    values={{
                                                        number: numbers?.[section.id] ?? i + 1,
                                                        title: section.name,
                                                    }}
                                                />
                                            </Typography>
                                            {section.description && <RichText doc={section.description} />}
                                            {section.id === FIELD_REQUESTS && (
                                                <Typography variant="textSm" color="textTextSecondary">
                                                    <FormattedMessage defaultMessage="Please update the following information we have about you." />
                                                </Typography>
                                            )}
                                            {documentCount > 0 && (
                                                <Typography variant="textSm" color="textTextSecondary">
                                                    <FormattedMessage
                                                        defaultMessage="{count, plural, one {# document requested} other {# documents requested}}"
                                                        values={{ count: documentCount }}
                                                    />
                                                </Typography>
                                            )}
                                        </Stack>
                                        {section.id === FIELD_REQUESTS ? (
                                            <FieldRequestsSection
                                                questions={shown}
                                                answers={answers}
                                                onChange={change}
                                            />
                                        ) : (
                                            <Stack
                                                sx={{
                                                    px: { xs: 2, sm: 3 },
                                                    "& > * + *": { borderTop: "1px solid", borderColor: "divider" },
                                                }}
                                            >
                                                {sectionQuestions.map((question, j) => {
                                                    const indented = nested.has(question.id);
                                                    return (
                                                        <Collapse
                                                            key={question.id}
                                                            in={shown.includes(question)}
                                                            appear={false}
                                                            unmountOnExit
                                                            sx={indented ? { "&&": { borderTop: 0 } } : undefined}
                                                        >
                                                            <Box sx={{ py: 3, pt: indented ? 0 : 3 }}>
                                                                <Box
                                                                    sx={
                                                                        indented
                                                                            ? {
                                                                                  pl: 2.5,
                                                                                  borderLeft: "2px solid",
                                                                                  borderColor: "divider",
                                                                              }
                                                                            : undefined
                                                                    }
                                                                >
                                                                    <Question
                                                                        question={question}
                                                                        number={numbers?.[question.id] ?? String(j + 1)}
                                                                        answer={answers[question.id]}
                                                                        onChange={(answer) =>
                                                                            change(question.id, answer)
                                                                        }
                                                                        review={reviews.find(
                                                                            (r) =>
                                                                                r.suggestion.questionKey === question.id
                                                                        )}
                                                                        onConfirm={() => confirm(question.id)}
                                                                        onReject={() => reject(question.id)}
                                                                        problems={found
                                                                            .filter((p) => p.question === question.id)
                                                                            .map((p) => p.kind)}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </Collapse>
                                                    );
                                                })}
                                                {shown.length === 0 && (
                                                    <Typography variant="textSm" color="textTextHelper" sx={{ py: 3 }}>
                                                        <FormattedMessage defaultMessage="No questions apply in this section based on your answers." />
                                                    </Typography>
                                                )}
                                            </Stack>
                                        )}
                                    </Box>
                                );
                            })}
                            {pending.length > 0 && (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        ...cardSx,
                                        display: { xs: "flex", lg: "none" },
                                        position: "sticky",
                                        bottom: 16,
                                        zIndex: 1,
                                        alignItems: "center",
                                        px: 2,
                                        py: 1.5,
                                        boxShadow: "0 4px 16px rgba(13, 16, 23, 0.12)",
                                    }}
                                >
                                    <AiStars fontSize="small" color="primary" />
                                    <Typography variant="textSm" sx={{ flex: 1 }}>
                                        <FormattedMessage
                                            defaultMessage="{count, plural, one {# suggestion} other {# suggestions}} to review"
                                            values={{ count: pending.length }}
                                        />
                                    </Typography>
                                    <Button size="small" variant="contained" onClick={nextPending}>
                                        <FormattedMessage defaultMessage="Next" />
                                    </Button>
                                </Stack>
                            )}
                            {!readOnly && onSubmit && (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{ justifyContent: "flex-end", alignItems: "center" }}
                                >
                                    {checked && pending.length > 0 && (
                                        <Typography variant="textSm" color="textTextError">
                                            <FormattedMessage defaultMessage="Confirm or remove the suggested answers before you submit." />
                                        </Typography>
                                    )}
                                    <Button variant="contained" onClick={submit} disabled={submitting}>
                                        <FormattedMessage defaultMessage="Submit" />
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </SuggestionsContext.Provider>
            </ReadOnlyContext.Provider>
        </DocumentTypesContext.Provider>
    );
};
